/**
 * Offline-safe route registry.
 *
 * Single source of truth for which routes the K9 PWA may render while
 * `authMode === 'offline'`. Anything not listed here will render the
 * `OfflineUnavailable` screen via `ProtectedRoute`.
 *
 * Routes here MUST be readable from cached app shell + locally available
 * data; they may not depend on live `/api/*` calls beyond the cached GETs
 * provided by the K9 service worker, and any mutations must be queued via
 * the K9 offline stores or `backgroundSyncService`.
 */

/**
 * Path prefixes that are allowed to render in offline mode.
 *
 * Order is irrelevant — each prefix is checked independently. Trailing
 * segments under a prefix (e.g. `/k9-ops/tracking/abc-123`) are also allowed.
 */
export const OFFLINE_SAFE_ROUTE_PREFIXES: readonly string[] = [
  '/k9-ops/quick-menu',
  '/k9-ops/dashboard',
  '/k9-ops/team',
  '/k9-ops/tracking',
  '/k9-ops/expense',
  '/k9-ops/training',
  '/k9-ops/vet',
  '/k9-ops/custody',
  '/k9-ops/activity',
  '/k9-ops/diagnostics',
  '/k9-ops/detection',
] as const;

function normalizePathname(pathname: string): string {
  if (!pathname) return '/';
  // Strip query/hash if a full URL or pathname-with-query was passed in.
  const queryIdx = pathname.indexOf('?');
  const hashIdx = pathname.indexOf('#');
  let end = pathname.length;
  if (queryIdx >= 0) end = Math.min(end, queryIdx);
  if (hashIdx >= 0) end = Math.min(end, hashIdx);
  let p = pathname.slice(0, end);
  // Collapse trailing slashes (except for the root path itself).
  if (p.length > 1 && p.endsWith('/')) {
    p = p.replace(/\/+$/, '');
  }
  return p || '/';
}

/**
 * Returns true if `pathname` matches any of the offline-safe prefixes.
 *
 * A path matches a prefix when it equals the prefix exactly OR it starts
 * with `${prefix}/` (so child routes are included). This avoids false
 * matches like `/k9-ops/tracking-admin` for the `/k9-ops/tracking` prefix.
 */
export function isRouteOfflineSafe(pathname: string): boolean {
  const p = normalizePathname(pathname);
  for (const prefix of OFFLINE_SAFE_ROUTE_PREFIXES) {
    if (p === prefix) return true;
    if (p.startsWith(`${prefix}/`)) return true;
  }
  return false;
}

/** Convenience: returns true when the path is anywhere under `/k9-ops`. */
export function isK9OpsRoute(pathname: string): boolean {
  const p = normalizePathname(pathname);
  return p === '/k9-ops' || p.startsWith('/k9-ops/');
}
